import { Room, RoomTypeOption } from '../types';

const API_BASE = '/api/v1';

export interface CreateRoomRequest {
  siteId: string;
  roomCode: string;
  roomName: string;
  roomType?: string;
  floor?: string;
  capacity?: number;
  description?: string;
}

export interface UpdateRoomRequest {
  roomCode?: string;
  roomName?: string;
  roomType?: string;
  floor?: string | null;
  capacity?: number | null;
  description?: string | null;
}

export interface RoomsListResponse {
  rooms: Room[];
  total: number;
}

export interface RoomTypesResponse {
  types: RoomTypeOption[];
}

export async function fetchRooms(siteId?: string): Promise<RoomsListResponse> {
  const url = siteId
    ? `${API_BASE}/rooms?siteId=${siteId}`
    : `${API_BASE}/rooms`;

  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch rooms: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchRoom(id: string): Promise<{ room: Room }> {
  const response = await fetch(`${API_BASE}/rooms/${id}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch room: ${response.statusText}`);
  }

  return response.json();
}

export async function fetchRoomTypes(): Promise<RoomTypesResponse> {
  const response = await fetch(`${API_BASE}/rooms/types`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch room types: ${response.statusText}`);
  }

  return response.json();
}

export async function createRoom(data: CreateRoomRequest): Promise<{ success: boolean; room: Room }> {
  const response = await fetch(`${API_BASE}/rooms`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to create room');
  }

  return response.json();
}

export async function updateRoom(id: string, data: UpdateRoomRequest): Promise<{ success: boolean; room: Room }> {
  const response = await fetch(`${API_BASE}/rooms/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to update room');
  }

  return response.json();
}

export async function deleteRoom(id: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/rooms/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to delete room');
  }

  return response.json();
}

export async function fetchRoomDevices(id: string): Promise<{ room: Room; devices: any[]; total: number }> {
  const response = await fetch(`${API_BASE}/rooms/${id}/devices`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch room devices: ${response.statusText}`);
  }

  return response.json();
}

export async function updateDeviceRoom(deviceId: string, roomId: string | null): Promise<any> {
  const response = await fetch(`${API_BASE}/devices/${deviceId}/room`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ roomId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Failed to update device room');
  }

  return response.json();
}
